import { useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { bookings } from "@fm/api";
import { EmbedProvider, useEmbedContext } from "../contexts/EmbedContext";
import {
  PaymentSuccessView,
  RetryPaymentFormInline,
  PaymentAuthenticationView,
} from "@fm/ui";
import { getApiBaseUrl } from "../lib/config";

interface PaymentSuccessContainerProps {
  rentalId: string;
  apiKey: string;
  fleetId: string;
  stripePublishableKey: string;
  apiBaseUrl?: string;
  theme?: "light" | "dark";
  onRetrySuccess?: (bookingId: string) => void;
  onRetryError?: (error: string) => void;
  onBackToSearch?: () => void;
}

function PaymentSuccessContainerInner({
  rentalId,
  apiBaseUrl,
  onRetrySuccess,
  onRetryError,
  onBackToSearch,
}: Omit<
  PaymentSuccessContainerProps,
  "apiKey" | "fleetId" | "stripePublishableKey" | "theme"
>) {
  const { apiKey, fleetId, stripePublishableKey } = useEmbedContext();

  const [rentalData, setRentalData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  // Load Stripe
  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  // Fetch booking details
  const fetchBookingDetails = useCallback(async () => {
    if (!rentalId || !apiKey || !fleetId) return;

    setLoading(true);
    setError(null);

    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      const result = await bookings.get(apiClient, fleetId, rentalId);

      if (result.data) {
        setRentalData(result.data);
      } else {
        setError("Failed to fetch booking details");
      }
    } catch {
      setError("An error occurred while fetching booking details");
    } finally {
      setLoading(false);
    }
  }, [rentalId, apiKey, fleetId]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  // Handle payment authentication (3D Secure)
  const handlePaymentAuthentication = async (clientSecret: string) => {
    setProcessingPayment(true);
    setPaymentError(null);

    try {
      const stripe = await loadStripe(stripePublishableKey);

      if (!stripe) {
        throw new Error("Stripe failed to load");
      }

      const { error } = await stripe.confirmPayment({
        clientSecret,
        redirect: "if_required",
      });

      if (error) {
        setPaymentError(error.message || "Payment authentication failed");
        return;
      }

      await fetchBookingDetails();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Payment authentication failed";
      setPaymentError(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle new payment method for failed payments
  const handleNewPaymentMethod = async (paymentMethod: {
    id: string;
    [key: string]: unknown;
  }) => {
    setProcessingPayment(true);
    setPaymentError(null);

    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      const paymentData = {
        stripe_payment_method_id: paymentMethod.id,
        card: {
          last4: (paymentMethod as any).card?.last4 || "",
          brand: (paymentMethod as any).card?.brand || "",
          exp_year: (paymentMethod as any).card?.exp_year || 0,
          exp_month: (paymentMethod as any).card?.exp_month || 0,
        },
      };

      const result = await bookings.retryPayment(
        apiClient,
        fleetId,
        rentalId,
        paymentData,
      );

      if (result.data) {
        if (result.data.client_secret) {
          const stripe = await loadStripe(stripePublishableKey);

          if (stripe) {
            const { error } = await stripe.confirmPayment({
              clientSecret: result.data.client_secret,
              redirect: "if_required",
            });

            if (error) {
              setPaymentError(error.message || "Payment authentication failed");
              return;
            }
          }
        }

        await fetchBookingDetails();
        setShowNewPaymentForm(false);
      } else {
        const errorMessage =
          (result as any).error?.message || "Failed to retry payment";
        setPaymentError(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to retry payment";
      setPaymentError(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  const clearErrors = () => {
    setPaymentError(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchBookingDetails()}
            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No rental data
  if (!rentalData) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Data</h1>
          <p className="text-gray-600">No booking information available.</p>
        </div>
      </div>
    );
  }

  // Show new payment form for failed payments
  if (rentalData.payment?.status === "failed" && showNewPaymentForm) {
    return (
      <div className="min-h-screen bg-[#f4f6f6] py-8 text-[#06131b]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-white ring-1 ring-[#e6e7e8] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Payment Failed
            </h1>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              Your payment could not be processed. Please try with a different
              payment method.
            </p>
          </div>

          <div className="space-y-6">
            {/* Rental Details Card */}
            <div className="bg-white rounded-xl border border-[#e6e7e8] overflow-hidden">
              <div className="border-b border-[#e6e7e8] px-6 py-4">
                <h2 className="text-base font-semibold text-[#06131b] flex items-center">
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Reservation Details
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Vehicle
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {rentalData.rental?.rentable?.name || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Pick up
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {rentalData.rental?.pick_up || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Return
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {rentalData.rental?.return || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Total
                      </h4>
                      <p className="text-gray-600 text-sm">
                        ${rentalData.rental?.total_amount || "0.00"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Form Card */}
            <div className="bg-white rounded-xl border border-[#e6e7e8] overflow-hidden">
              <div className="border-b border-[#e6e7e8] px-6 py-4">
                <h2 className="text-base font-semibold text-[#06131b] flex items-center">
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  New Payment Method
                </h2>
              </div>
              <div className="p-8">
                <div className="mb-6">
                  <p className="text-gray-600 text-center">
                    Please provide a new payment method to complete your
                    reservation.
                  </p>
                </div>

                {stripePromise && (
                  <Elements stripe={stripePromise}>
                    <RetryPaymentFormInline
                      onPaymentMethodCreated={handleNewPaymentMethod}
                      loading={processingPayment}
                      onClearErrors={clearErrors}
                    />
                  </Elements>
                )}

                <div className="mt-6 text-center">
                  <button
                    onClick={() => setShowNewPaymentForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium underline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show payment authentication required
  if (rentalData.requires_action && rentalData.client_secret) {
    return (
      <PaymentAuthenticationView
        clientSecret={rentalData.client_secret}
        onAuthenticate={handlePaymentAuthentication}
        processing={processingPayment}
        error={paymentError}
        onRetry={fetchBookingDetails}
      />
    );
  }

  // Show main success view
  return (
    <PaymentSuccessView
      rentalData={rentalData}
      onTryDifferentPayment={() => setShowNewPaymentForm(true)}
      onRetry={fetchBookingDetails}
      onBackToSearch={onBackToSearch}
    />
  );
}

export function PaymentSuccessContainer(props: PaymentSuccessContainerProps) {
  return (
    <EmbedProvider
      apiKey={props.apiKey}
      fleetId={props.fleetId}
      stripePublishableKey={props.stripePublishableKey}
      theme={props.theme}
    >
      <PaymentSuccessContainerInner {...props} />
    </EmbedProvider>
  );
}
